import React, { useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@nxsys/store';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { IconTrash, IconUpload } from '@nxsys/components/AppIconSvg';
import AppButton from '@nxsys/components/AppButton';
import {
  StyledModal,
  UploadArea,
  UploadIcon,
  UploadText,
  UploadSubtext,
  ProgressContainer,
  ProgressBar,
  ProgressText,
  FileInfo,
  FileName,
  FileSize,
  DeleteIcon,
  ButtonContainer,
  FileNameWrapper,
  FileInfoContainer,
  HeaderContainer,
  IconContainer,
  WrapperIconByType,
  StyledTitle,
} from './index.styled';
import { formatFileTypeToIcon } from '@nxsys/helpers/FileHelper';
import { Icons } from '@nxsys/types/icons';
import { defaultTheme } from '@nxsys/constants/defaultConfig';

export type FileType = 'spreadsheet' | 'spreadsheet,xml' | 'all';
export type FileData = {
  url: string;
  fileName: string;
  fileSize: number;
};

export interface UploadFileModalProps {
  open: boolean;
  onClose: () => void;
  onUploaded: (fileData: FileData) => void;
  fileType?: FileType;
  title?: string;
  maxFileSize?: number; // in MB
  loading?: boolean;
}

const SPREADSHEET_EXTENSIONS = ['.csv', '.xls', '.xlsx'];

const UploadFileModal: React.FC<UploadFileModalProps> = ({
  open,
  onClose,
  onUploaded,
  fileType = 'all',
  title,
  maxFileSize,
  loading = false,
}) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFileData, setUploadedFileData] = useState<{
    url: string;
    fileName: string;
    fileSize: number;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // File validation
  const validateFile = useCallback(
    (file: File): { valid: boolean; error?: string } => {
      // Check file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (maxFileSize && fileSizeMB > maxFileSize) {
        return {
          valid: false,
          error: `File size must be less than ${maxFileSize}MB`,
        };
      }

      // Check file type
      if (fileType === 'spreadsheet') {
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!SPREADSHEET_EXTENSIONS.includes(fileExtension)) {
          return {
            valid: false,
            error: 'Only spreadsheet files (.csv, .xls, .xlsx) are allowed',
          };
        }
      }

      if (fileType === 'spreadsheet,xml') {
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (
          !SPREADSHEET_EXTENSIONS.includes(fileExtension) &&
          fileExtension !== '.xml'
        ) {
          return {
            valid: false,
            error:
              'Only spreadsheet files (.csv, .xls, .xlsx) and XML files are allowed',
          };
        }
      }

      return { valid: true };
    },
    [fileType, maxFileSize],
  );

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Handle modal close
  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setDragActive(false);
    setUploadProgress(0);
    setIsUploading(false);
    setUploadError(null);
    setUploadedFileData(null);
    onClose();
  }, [onClose]);

  // Handle upload
  const handleUpload = useCallback(
    async (file: File) => {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError(null);

      // Simulate progress update
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 80) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 20;
        });
      }, 200);

      try {
        const result = await dispatch(
          commonActions.uploadFileToS3({ file }),
        ).unwrap();

        if (result) {
          const url = result.url;
          // NX-6056: the thunk returns the S3 object key; store it as-is.
          const fileUrl = url;

          setUploadProgress(100);

          // Store the uploaded file data for confirmation
          setUploadedFileData({
            url: fileUrl,
            fileName: file.name,
            fileSize: file.size,
          });
        } else {
          throw new Error('Upload failed');
        }
      } catch (error: any) {
        setUploadError(error.message || 'Upload failed. Please try again.');
        clearInterval(progressInterval);
      } finally {
        setIsUploading(false);
      }
    },
    [dispatch, onUploaded, handleClose],
  );

  // Handle file selection and auto-upload
  const handleFileSelect = useCallback(
    (file: File) => {
      setUploadError(null);
      const validation = validateFile(file);

      if (!validation.valid) {
        setUploadError(validation.error || 'Invalid file');
        return;
      }

      setSelectedFile(file);
      // Auto-upload the file immediately after selection
      handleUpload(file);
    },
    [validateFile, handleUpload],
  );

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileSelect(e.dataTransfer.files[0]);
      }
    },
    [handleFileSelect],
  );

  // Handle file input change
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        handleFileSelect(e.target.files[0]);
      }
    },
    [handleFileSelect],
  );

  // Handle file removal
  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setUploadError(null);
    setUploadedFileData(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Handle confirm - trigger callback and close modal
  const handleConfirm = useCallback(() => {
    if (uploadedFileData) {
      onUploaded(uploadedFileData);
      handleClose();
    }
  }, [uploadedFileData, onUploaded, handleClose]);

  // Click to upload
  const handleClickUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Get file type text for display
  const getFileTypeText = () => {
    if (fileType === 'spreadsheet') {
      return 'CSV, XLS, XLSX';
    }
    if (fileType === 'spreadsheet,xml') {
      return 'CSV, XLS, XLSX, XML';
    }
    return 'CSV, XLS, XLSX, SVG, PNG, JPG or GIF, ...';
  };

  const getAcceptAttribute = () => {
    if (fileType === 'spreadsheet') {
      return '.csv,.xls,.xlsx';
    }
    if (fileType === 'spreadsheet,xml') {
      return '.csv,.xls,.xlsx,.xml';
    }
    return '*/*';
  };

  return (
    <StyledModal
      title={null}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={600}
      centered
    >
      <HeaderContainer>
        <IconContainer>
          <WrapperIconByType>
            <Icons.IconFileImport
              color={defaultTheme.theme.palette.primary[500]}
            />
          </WrapperIconByType>
        </IconContainer>
        <StyledTitle>{title || messages['common.importFile']}</StyledTitle>
      </HeaderContainer>
      {!selectedFile ? (
        <UploadArea
          $dragActive={dragActive}
          $hasError={!!uploadError}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClickUpload}
        >
          <UploadIcon>
            <IconUpload />
          </UploadIcon>
          <UploadText>
            <span>Click to upload</span> or drag and drop
          </UploadText>
          <UploadSubtext>
            {getFileTypeText()} {maxFileSize && `(max. ${maxFileSize}MB)`}
          </UploadSubtext>
          {uploadError && <UploadSubtext $error>{uploadError}</UploadSubtext>}
          <input
            ref={fileInputRef}
            type="file"
            accept={getAcceptAttribute()}
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
        </UploadArea>
      ) : (
        <>
          <FileInfo>
            <FileInfoContainer>
              {formatFileTypeToIcon(selectedFile.type)}
              <FileNameWrapper>
                <FileName>{selectedFile.name}</FileName>
                <DeleteIcon onClick={handleRemoveFile}>
                  <IconTrash />
                </DeleteIcon>
              </FileNameWrapper>
            </FileInfoContainer>
            <FileSize>{formatFileSize(selectedFile.size)}</FileSize>
            {isUploading && (
              <>
                <ProgressContainer>
                  <ProgressBar $progress={uploadProgress} />
                </ProgressContainer>
                <ProgressText>{Math.round(uploadProgress)}%</ProgressText>
              </>
            )}
          </FileInfo>

          {uploadError && <UploadSubtext $error>{uploadError}</UploadSubtext>}
        </>
      )}

      <ButtonContainer>
        <AppButton
          name={messages['common.cancel'] || 'Cancel'}
          variant="outlined"
          onClick={handleClose}
          disabled={isUploading || loading}
          className="w-full"
        />
        <AppButton
          name={messages['common.confirm'] || 'Confirm'}
          onClick={handleConfirm}
          disabled={!uploadedFileData || isUploading || loading}
          loading={isUploading || loading}
          className="w-full"
        />
      </ButtonContainer>
    </StyledModal>
  );
};

export default UploadFileModal;
